/**
 * Every finished run, with the inputs that made it: the time and falls of each player, and each
 * player's tape from the course's start, so a run on the leaderboard's list can be replayed (the
 * simulation is deterministic) and the fastest one can run again as the best-time ghost. Kept in
 * localStorage beside the scoreboard, newest first, at most RUNS_PER_COURSE per course layout; the
 * fastest run of each name is never dropped. Storage that is missing or full keeps the runs of
 * this session in memory.
 */

import { cleanPlayerName } from "@lache/relay/names";
import type { Tape } from "@lache/sim";
import { formatTime } from "./scores";

export interface RunPlayer {
  name: string;
  /** Headband color, an index into HEADBANDS. */
  band: number;
  /** Run time, s, or null when this player did not finish (a race that ended first). */
  time: number | null;
  falls: number;
  /** The tick the run started (the start line crossing), so a replay can skip the warm-up and a ghost can align. */
  startTick: number;
  /** The inputs from the course's start (respawn 0) to the finish, or to the end of the race. */
  tape: Tape;
}

export interface RunRecord {
  id: string;
  /** Course name, and the board key (name plus a hash of the layout, `courseKey`). */
  course: string;
  key: string;
  /** When the run finished, epoch ms. */
  date: number;
  /** Tuning hash the run was made with; a replay under another tuning may not match. */
  tuning: string;
  mode: "single" | "race";
  players: RunPlayer[];
}

export const RUNS_PER_COURSE = 40;
const RUNS_KEY = "lache.runs.v1";

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function isTape(v: unknown): v is Tape {
  if (typeof v !== "object" || v === null) return false;
  const t = v as Record<string, unknown>;
  return typeof t.course === "string" && Array.isArray(t.frames);
}

function isPlayer(v: unknown): v is RunPlayer {
  if (typeof v !== "object" || v === null) return false;
  const p = v as Record<string, unknown>;
  if (typeof p.name !== "string" || typeof p.falls !== "number" || typeof p.startTick !== "number")
    return false;
  if (p.time !== null && (typeof p.time !== "number" || !Number.isFinite(p.time))) return false;
  if (typeof p.band !== "number") p.band = 0;
  return isTape(p.tape);
}

function isRecord(v: unknown): v is RunRecord {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.id !== "string" || typeof r.course !== "string" || typeof r.key !== "string") return false;
  if (typeof r.date !== "number" || (r.mode !== "single" && r.mode !== "race")) return false;
  if (typeof r.tuning !== "string") r.tuning = "";
  return Array.isArray(r.players) && r.players.length > 0 && r.players.every(isPlayer);
}

/** Newest first. */
function byDate(a: RunRecord, b: RunRecord): number {
  return b.date - a.date;
}

/** Keep every run and tape; older or externally supplied names get the same safe display fallback. */
function safeRecord(record: RunRecord): RunRecord {
  return {
    ...record,
    players: record.players.map((player) => ({ ...player, name: cleanPlayerName(player.name) })),
  };
}

export function newRunId(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 0xffffff).toString(36)}`;
}

export class RunStore {
  private runs: Record<string, RunRecord[]> = {};
  private readonly unsaved = new Map<string, RunRecord>();
  private readonly removals = new Set<string>();
  private persisted = new Set<string>();

  constructor(private readonly storage: Storage | null = safeStorage()) {
    this.runs = this.load() ?? {};
    this.rememberPersisted(this.runs);
  }

  /** Only an authoritative read or successful write can retire deletion markers. */
  private rememberPersisted(runs: Record<string, RunRecord[]>): void {
    this.persisted = new Set(
      Object.values(runs)
        .flat()
        .map((record) => record.id),
    );
    for (const id of this.removals) if (!this.persisted.has(id)) this.removals.delete(id);
  }

  private load(): Record<string, RunRecord[]> | null {
    if (!this.storage) return null;
    try {
      const raw = this.storage.getItem(RUNS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== "object" || parsed === null) return null;
      const out: Record<string, RunRecord[]> = {};
      for (const [key, list] of Object.entries(parsed as Record<string, unknown>)) {
        if (!Array.isArray(list)) continue;
        out[key] = list.filter(isRecord).map(safeRecord).sort(byDate);
      }
      return out;
    } catch {
      return null;
    }
  }

  /** Refresh shared history without losing local writes that a full or blocked store rejected. */
  private sync(): void {
    const shared = this.load();
    if (!shared) return;
    this.rememberPersisted(shared);
    for (const [key, list] of Object.entries(shared)) {
      shared[key] = list.filter((record) => !this.removals.has(record.id) && !this.unsaved.has(record.id));
    }
    for (const record of this.unsaved.values()) (shared[record.key] ??= []).push(record);
    this.runs = shared;
    for (const key of Object.keys(this.runs)) this.trim(key);
  }

  /** Save; when storage is full, drop the oldest runs that hold no best time and try again. */
  private save(): void {
    if (!this.storage) return;
    // Disk pruning must not remove replay choices from this still-open session. A blocked store
    // may reject every attempt; work on a copy until a smaller snapshot can be persisted.
    const saved = { ...this.runs };
    // Each retry removes one retained replay; always reach a fitting snapshot even after many
    // quota-limited saves, while bounding work by the current history rather than retrying forever.
    const attempts = Object.values(saved).reduce((count, list) => count + list.length, 1);
    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        this.storage.setItem(RUNS_KEY, JSON.stringify(saved));
        // Replays trimmed only to fit disk remain available in this session and may save later.
        this.rememberPersisted(saved);
        this.unsaved.clear();
        for (const record of Object.values(this.runs).flat()) {
          if (!this.persisted.has(record.id)) this.unsaved.set(record.id, record);
        }
        this.removals.clear();
        return;
      } catch {
        if (!this.dropOldest(saved)) return;
      }
    }
  }

  /** Drop the oldest run that is nobody's best, across every course. Returns false when none is left. */
  private dropOldest(runs: Record<string, RunRecord[]>): boolean {
    let oldestKey = "";
    let oldest: RunRecord | null = null;
    for (const [key, list] of Object.entries(runs)) {
      const pinned = this.pinnedIds(list);
      for (const r of list) {
        if (pinned.has(r.id)) continue;
        if (!oldest || r.date < oldest.date) {
          oldest = r;
          oldestKey = key;
        }
      }
    }
    if (!oldest) return false;
    runs[oldestKey] = runs[oldestKey]!.filter((r) => r !== oldest);
    return true;
  }

  /** The ids of the runs holding each name's fastest finished time in `list`. */
  private pinnedIds(list: RunRecord[]): Set<string> {
    const best = new Map<string, { time: number; id: string }>();
    for (const r of list) {
      for (const p of r.players) {
        if (p.time === null) continue;
        const cur = best.get(p.name);
        if (!cur || p.time < cur.time) best.set(p.name, { time: p.time, id: r.id });
      }
    }
    return new Set([...best.values()].map((b) => b.id));
  }

  private trim(key: string): void {
    const list = (this.runs[key] ?? []).sort(byDate);
    if (list.length <= RUNS_PER_COURSE) {
      this.runs[key] = list;
      return;
    }
    const pinned = this.pinnedIds(list);
    const kept: RunRecord[] = [];
    let unpinned = 0;
    const room = RUNS_PER_COURSE - pinned.size;
    for (const r of list) {
      if (pinned.has(r.id)) kept.push(r);
      else if (unpinned < room) {
        kept.push(r);
        unpinned++;
      }
    }
    this.runs[key] = kept.sort(byDate);
  }

  /** The runs of a course layout, newest first. */
  list(key: string): RunRecord[] {
    return this.runs[key] ?? [];
  }

  get(id: string): RunRecord | null {
    for (const list of Object.values(this.runs)) {
      const r = list.find((x) => x.id === id);
      if (r) return r;
    }
    return null;
  }

  /** Add a run (or replace the one with its id) and save. */
  put(record: RunRecord): void {
    this.sync();
    record = safeRecord(record);
    this.removals.delete(record.id);
    this.unsaved.set(record.id, record);
    const list = (this.runs[record.key] ?? []).filter((r) => r.id !== record.id);
    list.push(record);
    this.runs[record.key] = list;
    this.trim(record.key);
    const retained = new Set(this.list(record.key).map((run) => run.id));
    for (const [id, pending] of this.unsaved) {
      if (pending.key === record.key && !retained.has(id)) this.unsaved.delete(id);
    }
    this.save();
  }

  remove(id: string): void {
    this.sync();
    this.unsaved.delete(id);
    if (this.persisted.has(id)) this.removals.add(id);
    for (const [key, list] of Object.entries(this.runs)) this.runs[key] = list.filter((r) => r.id !== id);
    this.save();
  }

  /** The fastest finished run of `name` on a course layout: the record and the player's index in it. */
  best(key: string, name: string): { record: RunRecord; player: number } | null {
    let out: { record: RunRecord; player: number } | null = null;
    let bestTime = Number.POSITIVE_INFINITY;
    for (const record of this.list(key)) {
      record.players.forEach((p, i) => {
        if (p.name === name && p.time !== null && p.time < bestTime) {
          bestTime = p.time;
          out = { record, player: i };
        }
      });
    }
    return out;
  }
}

/** A run's moment in words: "today 14:03", "yesterday 09:12", "Sep 18 21:40". */
export function formatWhen(ms: number, now = Date.now()): string {
  const d = new Date(ms);
  const today = new Date(now);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return `today ${hh}:${mm}`;
  const yesterday = new Date(now - 86_400_000);
  if (sameDay(d, yesterday)) return `yesterday ${hh}:${mm}`;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const year = d.getFullYear() === today.getFullYear() ? "" : ` ${d.getFullYear()}`;
  return `${months[d.getMonth()]} ${d.getDate()}${year} ${hh}:${mm}`;
}

/** One row of the runs list: a player's time in a run, newest run first, player 1 before player 2. */
export interface RunRow {
  id: string;
  player: number;
  label: string;
  detail: string;
}

export function runRows(records: RunRecord[], now = Date.now()): RunRow[] {
  const rows: RunRow[] = [];
  for (const r of records) {
    r.players.forEach((p, i) => {
      const who = r.mode === "race" ? `race · P${i + 1} ${p.name}` : p.name;
      rows.push({
        id: r.id,
        player: i,
        label: `${formatWhen(r.date, now)} · ${who}`,
        detail: p.time === null ? "did not finish" : formatTime(p.time),
      });
    });
  }
  return rows;
}
